<template>
  <div class="p-6 max-w-6xl mx-auto">
    <div class="mb-8 flex items-center justify-between">
      <div>
        <h2 class="text-3xl font-extrabold tracking-tight text-gray-900">
          Клиники
        </h2>
        <p class="text-sm text-gray-500 mt-1">
          Управление клиниками, статусами и врачами
        </p>
      </div>
      <div class="hidden md:block text-right">
        <div class="text-xs uppercase tracking-wide text-gray-500">
          Всего клиник
        </div>
        <div class="text-2xl font-bold text-gray-800">{{ clinics.length }}</div>
      </div>
    </div>

    <!-- Вкладки по модулям -->
    <div class="mb-8 flex flex-wrap gap-2 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-sm">
      <button
        v-for="tab in clinicTabs"
        :key="tab.value"
        type="button"
        @click="setActiveTab(tab.value)"
        class="inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition"
        :class="activeTab === tab.value ? tab.activeClass : 'text-gray-600 hover:bg-gray-50'"
      >
        <span class="h-2 w-2 rounded-full" :class="tab.dotClass"></span>
        {{ tab.label }}
        <span
          class="rounded-full px-2 py-0.5 text-xs"
          :class="activeTab === tab.value ? 'bg-white/25' : 'bg-gray-100 text-gray-500'"
        >
          {{ tabCounts[tab.value] }}
        </span>
      </button>
    </div>

    <!-- Создание клиники -->
    <form
      @submit.prevent="createClinic"
      class="mb-10 rounded-2xl border border-gray-200 bg-white/70 backdrop-blur p-6 shadow-sm"
    >
      <div class="mb-4">
        <h3 class="text-lg font-semibold text-gray-800">Добавить клинику</h3>
        <p class="text-sm text-gray-500">Заполните поля и нажмите «Добавить»</p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          v-model="form.name"
          type="text"
          placeholder="Название клиники"
          class="w-full rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500 px-4 py-2.5"
        />
        <select
          v-model="form.type"
          class="w-full rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500 px-4 py-2.5"
        >
          <option value="" disabled>Тип клиники</option>
          <option value="clinic">ПМСП (Клиника)</option>
          <option value="stationary">Стационар</option>
          <option value="budget">Бюджет</option>
        </select>
        <input
          v-model="form.address"
          type="text"
          placeholder="Адрес"
          class="w-full rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500 px-4 py-2.5"
        />
      </div>

      <div class="mt-4">
        <button
          type="submit"
          class="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-white font-semibold hover:bg-teal-700 active:scale-[.99] transition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Добавить
        </button>
      </div>
    </form>

    <section class="mb-10 rounded-2xl border border-emerald-200 bg-white/80 p-6 shadow-sm">
      <div class="mb-4 flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 class="text-lg font-semibold text-gray-800">Индивидуальный врач</h3>
          <p class="text-sm text-gray-500">Вход по имени врача и паролю без выбора клиники</p>
        </div>
        <div class="text-sm font-semibold text-emerald-700">
          Всего: {{ filteredIndividualDoctors.length }}
        </div>
      </div>

      <form @submit.prevent="createIndividualDoctor" class="grid grid-cols-1 md:grid-cols-6 gap-4">
        <input
          v-model="individualForm.full_name"
          type="text"
          placeholder="Имя врача"
          class="w-full rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 px-4 py-2.5"
        />
        <input
          v-model="individualForm.organization_name"
          type="text"
          placeholder="Организация"
          class="w-full rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 px-4 py-2.5"
        />
        <select
          v-model="individualForm.panel_type"
          class="w-full rounded-xl border-gray-200 bg-white focus:border-emerald-500 focus:ring-emerald-500 px-4 py-2.5"
        >
          <option value="clinic">ПМСП</option>
          <option value="stationary">Стационар</option>
          <option value="budget">Бюджет</option>
        </select>
        <input
          v-model="individualForm.password"
          type="password"
          placeholder="Пароль"
          class="w-full rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 px-4 py-2.5"
        />
        <input
          v-model="individualForm.password_confirmation"
          type="password"
          placeholder="Повторите пароль"
          class="w-full rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 px-4 py-2.5"
        />
        <button
          type="submit"
          :disabled="individualPending"
          class="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-5 py-2.5 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
        >
          {{ individualPending ? "Добавляю..." : "Добавить" }}
        </button>
      </form>

      <div v-if="individualError" class="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ individualError }}
      </div>

      <div v-if="filteredIndividualDoctors.length" class="mt-5 overflow-hidden rounded-xl border border-gray-200">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-gray-600">
            <tr>
              <th class="px-4 py-3 font-semibold">Имя врача</th>
              <th class="px-4 py-3 font-semibold">Организация</th>
              <th class="px-4 py-3 font-semibold">Панель</th>
              <th class="px-4 py-3 font-semibold">Статус</th>
              <th class="px-4 py-3 text-right font-semibold">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="doctor in filteredIndividualDoctors"
              :key="doctor.id"
              class="border-t border-gray-100"
            >
              <td class="px-4 py-3 font-medium text-gray-900">{{ doctor.full_name }}</td>
              <td class="px-4 py-3 text-gray-700">{{ doctor.organization_name || "—" }}</td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset"
                  :class="getClinicTypeBadgeClass(doctor.panel_type)"
                >
                  {{ getClinicTypeLabel(doctor.panel_type) }}
                </span>
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                  :class="doctor.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'"
                >
                  {{ doctor.is_active ? "Активен" : "Отключён" }}
                </span>
              </td>
              <td class="px-4 py-3 text-right">
                <button
                  type="button"
                  :disabled="individualDeleting[doctor.id]"
                  @click="deleteIndividualDoctor(doctor)"
                  class="inline-flex items-center justify-center rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {{ individualDeleting[doctor.id] ? "Удаляю..." : "Удалить" }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Список клиник -->
    <div v-if="filteredClinics.length" class="space-y-5">
      <div
        v-for="clinic in filteredClinics"
        :key="clinic.id"
        class="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition"
      >
        <div class="h-1 w-full" :class="getClinicTypeGradient(clinic)" />

        <div class="p-5 md:p-6">
          <div
            class="flex flex-col md:flex-row md:items-start md:justify-between gap-4"
          >
            <!-- Левая колонка -->
            <div class="min-w-0">
              <div class="flex items-center gap-2.5 flex-wrap">
                <span
                  class="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ring-1 ring-inset"
                  :class="
                    clinic.is_active
                      ? 'bg-green-50 text-green-700 ring-green-600/20'
                      : 'bg-gray-50 text-gray-600 ring-gray-500/20'
                  "
                  :title="
                    clinic.is_active ? 'Клиника активна' : 'Клиника отключена'
                  "
                >
                  <span
                    class="inline-block w-1.5 h-1.5 rounded-full"
                    :class="clinic.is_active ? 'bg-green-500' : 'bg-gray-400'"
                  />
                  {{ clinic.is_active ? "Активна" : "Выключена" }}
                </span>

                <!-- Бейдж типа клиники -->
                <span
                  class="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full ring-1 ring-inset"
                  :class="getClinicTypeBadgeClass(clinic.type)"
                >
                  <svg
                    v-if="clinic.type === 'clinic'"
                    class="h-3.5 w-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"
                    />
                  </svg>
                  <svg
                    v-else
                    class="h-3.5 w-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5zm0 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm-1-10v5h2v-5h-2zm0 7v2h2v-2h-2z"
                    />
                  </svg>
                  {{ getClinicTypeLabel(clinic.type) }}
                </span>

                <h3 class="text-xl font-bold text-gray-900 truncate">
                  {{ clinic.name }}
                </h3>
              </div>

              <div class="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div
                  v-if="clinic.address"
                  class="flex items-start gap-2 text-gray-700"
                >
                  <svg
                    class="h-4 w-4 mt-0.5 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7Zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5Z"
                    />
                  </svg>
                  <span class="truncate">{{ clinic.address }}</span>
                </div>
                <div
                  v-if="clinic.phone"
                  class="flex items-start gap-2 text-gray-700"
                >
                  <svg
                    class="h-4 w-4 mt-0.5 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M6.62 10.79a15.093 15.093 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V21c0 .55-.45 1-1 1C10.85 22 2 13.15 2 2c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.24.2 2.45.57 3.57.11.35.03.74-.24 1.02l-2.2 2.2z"
                    />
                  </svg>
                  <span class="truncate">{{ clinic.phone }}</span>
                </div>
                <div class="flex items-start gap-2 text-gray-700">
                  <svg
                    class="h-4 w-4 mt-0.5 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M16 11c1.66 0 2.99-1.34 2.99-3L19 5c0-1.66-1.34-3-3-3S13 3.34 13 5v3c0 1.66 1.34 3 3 3zM6 11c1.66 0 2.99-1.34 2.99-3L9 5c0-1.66-1.34-3-3-3S3 3.34 3 5v3c0 1.66 1.34 3 3 3zm10 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5c0-2.33-4.67-3.5-7-3.5zM6 13c-2.33 0-7 1.17-7 3.5V20h7v-3.5C6 14.17 6 13 6 13z"
                    />
                  </svg>
                  <span
                    >Врачей:
                    <b>
                      {{ clinic.doctors_count }}
                      <template v-if="isBudgetClinic(clinic)">/5</template>
                    </b>
                  </span
                  >
                </div>
              </div>
            </div>

            <!-- Правая колонка: действия -->
            <div
              class="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3"
            >
              <label
                class="inline-flex items-center cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  class="sr-only"
                  :checked="clinic.is_active"
                  :disabled="pending[clinic.id]"
                  @change="toggleActive(clinic)"
                />
                <span
                  class="w-12 h-7 flex items-center rounded-full p-1 transition"
                  :class="clinic.is_active ? 'bg-green-500' : 'bg-gray-300'"
                >
                  <span
                    class="bg-white w-5 h-5 rounded-full shadow transform transition"
                    :class="clinic.is_active ? 'translate-x-5' : ''"
                  />
                </span>
                <span class="ml-2 text-sm text-gray-700">
                  {{ clinic.is_active ? "Включена" : "Отключена" }}
                  <span v-if="pending[clinic.id]" class="ml-1 text-gray-400"
                    >(…)</span
                  >
                </span>
              </label>

              <button
                @click="goToAddDoctor(clinic)"
                :disabled="isBudgetFull(clinic)"
                :title="getAddDoctorButtonTitle(clinic)"
                class="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Добавить врача
              </button>

              <router-link
                :to="{ name: 'ClinicDoctors', params: { clinicId: clinic.id } }"
                class="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                Врачи клиники
              </router-link>

              <button
                @click="deleteClinic(clinic.id)"
                class="inline-flex items-center justify-center rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Удалить
              </button>

              <!-- Подписка -->
              <div
                class="flex min-w-[9rem] flex-col items-center justify-center rounded-xl px-3 py-2 text-center text-sm font-bold"
                :class="getSubscriptionClass(clinic)"
              >
                <template v-if="editingSubscription[clinic.id] !== undefined">
                  <input
                    v-model="editingSubscription[clinic.id]"
                    type="date"
                    class="w-full rounded-lg border-gray-200 px-2 py-1 text-xs font-normal text-gray-800"
                  />
                  <div class="mt-1.5 flex gap-2 text-xs">
                    <button
                      type="button"
                      :disabled="subscriptionSaving[clinic.id]"
                      @click="saveSubscription(clinic)"
                      class="underline disabled:opacity-50"
                    >
                      {{ subscriptionSaving[clinic.id] ? "..." : "сохранить" }}
                    </button>
                    <button
                      type="button"
                      :disabled="subscriptionSaving[clinic.id]"
                      @click="cancelEditSubscription(clinic)"
                      class="underline opacity-80"
                    >
                      отмена
                    </button>
                  </div>
                </template>
                <template v-else>
                  <span>
                    {{
                      clinic.subscription_until
                        ? `Доступ до ${formatDate(clinic.subscription_until)}г.`
                        : "Срок не задан"
                    }}
                  </span>
                  <button
                    type="button"
                    @click="startEditSubscription(clinic)"
                    class="mt-0.5 text-xs underline"
                  >
                    изменить
                  </button>
                </template>
              </div>
            </div>
          </div>

          <div
            v-if="categories.length"
            class="mt-5 rounded-xl border border-dashed border-gray-200 p-4"
          >
            <div class="flex flex-col md:flex-row md:items-end gap-3">
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Быстрое добавление врача</label
                >
                <select
                  v-model="selectedCategory"
                  class="w-full rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500 px-4 py-2.5"
                >
                  <option disabled value="">Выберите специализацию</option>
                  <option
                    v-for="cat in categories"
                    :key="cat.id"
                    :value="cat.id"
                  >
                    {{ cat.name }}
                  </option>
                </select>
              </div>
              <button
                @click="quickAddDoctor(clinic)"
                :disabled="!selectedCategory || isBudgetFull(clinic)"
                :title="isBudgetFull(clinic) ? 'Для бюджетной клиники доступно максимум 5 врачей' : ''"
                class="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-white font-semibold hover:bg-green-700 disabled:opacity-50"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 11h-6V5h-2v6H5v2h6v6h2v-6h6z" />
                </svg>
                Добавить с категорией
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="pending[clinic.id]"
          class="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center"
        >
          <div
            class="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-teal-600"
          ></div>
        </div>
      </div>
    </div>

    <div v-else class="text-gray-500 text-center">
      {{ activeTab === "all" ? "Клиники не найдены." : "В этом модуле клиник нет." }}
    </div>
  </div>
</template>

<script>
import axios from "@/axios";
import clinicService from "@/services/clinicService";
import doctorService from "@/services/doctorService";

const CLINIC_TYPES = ["clinic", "stationary", "budget"];
const TAB_VALUES = ["all", ...CLINIC_TYPES];

export default {
  name: "ClinicsView",
  data() {
    return {
      clinics: [],
      activeTab: "all",
      individualDoctors: [],
      categories: [],
      selectedCategory: "",
      pending: {},
      editingSubscription: {},
      subscriptionSaving: {},
      individualPending: false,
      individualDeleting: {},
      individualError: "",
      individualForm: {
        full_name: "",
        organization_name: "",
        panel_type: "clinic",
        password: "",
        password_confirmation: "",
      },
      form: {
        name: "",
        type: "",
        address: "",
        city_id: null,
        phone: "",
        email: "",
      },
    };
  },
  computed: {
    clinicTabs() {
      return [
        { value: "all", label: "Все", dotClass: "bg-gray-400", activeClass: "bg-gray-800 text-white shadow" },
        { value: "clinic", label: "ПМСП", dotClass: "bg-blue-500", activeClass: "bg-gradient-to-r from-blue-500 to-teal-500 text-white shadow" },
        { value: "stationary", label: "Стационар", dotClass: "bg-red-500", activeClass: "bg-gradient-to-r from-red-500 to-red-600 text-white shadow" },
        { value: "budget", label: "Бюджет", dotClass: "bg-amber-500", activeClass: "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow" },
      ];
    },
    tabCounts() {
      const counts = { all: this.clinics.length, clinic: 0, stationary: 0, budget: 0 };
      this.clinics.forEach((c) => {
        counts[this.normalizeType(c.type)] += 1;
      });
      return counts;
    },
    filteredClinics() {
      if (this.activeTab === "all") return this.clinics;
      return this.clinics.filter((c) => this.normalizeType(c.type) === this.activeTab);
    },
    filteredIndividualDoctors() {
      if (this.activeTab === "all") return this.individualDoctors;
      return this.individualDoctors.filter(
        (d) => this.normalizeType(d.panel_type) === this.activeTab
      );
    },
  },
  watch: {
    "$route.query.type": {
      immediate: true,
      handler(type) {
        this.activeTab = TAB_VALUES.includes(type) ? type : "all";
      },
    },
    activeTab: {
      immediate: true,
      handler(tab) {
        if (CLINIC_TYPES.includes(tab)) {
          this.form.type = tab;
          this.individualForm.panel_type = tab;
        }
      },
    },
  },
  methods: {
    normalizeType(type) {
      // Клиники без типа отображаются как ПМСП (как и в бейдже)
      return CLINIC_TYPES.includes(type) ? type : "clinic";
    },
    setActiveTab(tab) {
      if (tab === this.activeTab) return;
      const query = { ...this.$route.query };
      if (tab === "all") delete query.type;
      else query.type = tab;
      this.$router.replace({ query });
    },
    defaultTypeForTab() {
      return CLINIC_TYPES.includes(this.activeTab) ? this.activeTab : "";
    },
    getClinicTypeLabel(type) {
      if (type === "stationary") return "Стационар";
      if (type === "budget") return "Бюджет";
      return "ПМСП";
    },
    getClinicTypeBadgeClass(type) {
      if (type === "stationary") {
        return "bg-red-50 text-red-700 ring-red-600/20";
      }
      if (type === "budget") {
        return "bg-amber-50 text-amber-700 ring-amber-600/20";
      }
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    },
    getClinicTypeGradient(clinic) {
      if (!clinic.is_active) {
        return "bg-gray-300";
      }
      if (clinic.type === "budget") {
        return "bg-gradient-to-r from-amber-500 to-orange-500";
      }
      return clinic.type === "stationary"
        ? "bg-gradient-to-r from-red-500 to-red-600"
        : "bg-gradient-to-r from-blue-500 to-teal-500";
    },
    isBudgetClinic(clinic) {
      return clinic?.type === "budget";
    },
    isBudgetFull(clinic) {
      return this.isBudgetClinic(clinic) && Number(clinic?.doctors_count || 0) >= 5;
    },
    getAddDoctorButtonTitle(clinic) {
      if (!this.isBudgetFull(clinic)) return "";
      return "Для бюджетной клиники доступно максимум 5 врачей";
    },
    goToAddDoctor(clinic) {
      if (this.isBudgetFull(clinic)) {
        alert("В бюджетную клинику можно добавить максимум 5 врачей");
        return;
      }

      this.$router.push({
        name: "AddClinicDoctor",
        params: { clinicId: clinic.id },
        query: { clinicName: clinic.name },
      });
    },
    async fetchClinics() {
      this.clinics = await clinicService.fetchAll();
    },
    async fetchIndividualDoctors() {
      try {
        this.individualDoctors = await doctorService.fetchIndividualDoctors();
      } catch (err) {
        console.error("Ошибка загрузки индивидуальных врачей:", err);
        this.individualDoctors = [];
      }
    },
    async fetchCategories() {
      try {
        const res = await axios.get("/doctor/category");
        this.categories = res.data.categories || [];
      } catch (err) {
        console.error("Ошибка загрузки категорий:", err);
        this.categories = [];
      }
    },
    async createClinic() {
      try {
        await clinicService.create(this.form);
        await this.fetchClinics();
        this.form = {
          name: "",
          type: this.defaultTypeForTab(),
          address: "",
          city_id: null,
          phone: "",
          email: "",
        };
      } catch {
        alert("Ошибка при создании клиники");
      }
    },
    async createIndividualDoctor() {
      this.individualError = "";
      const fullName = this.individualForm.full_name.trim();
      const organizationName = this.individualForm.organization_name.trim();

      if (!fullName || !this.individualForm.panel_type || !this.individualForm.password || !this.individualForm.password_confirmation) {
        this.individualError = "Заполните имя врача, панель и пароль";
        return;
      }

      if (this.individualForm.password.length < 6) {
        this.individualError = "Пароль должен быть не короче 6 символов";
        return;
      }

      if (this.individualForm.password !== this.individualForm.password_confirmation) {
        this.individualError = "Пароли не совпадают";
        return;
      }

      try {
        this.individualPending = true;
        await doctorService.createIndividualDoctor({
          full_name: fullName,
          organization_name: organizationName || null,
          panel_type: this.individualForm.panel_type,
          password: this.individualForm.password,
          password_confirmation: this.individualForm.password_confirmation,
          is_active: true,
        });

        this.individualForm = {
          full_name: "",
          organization_name: "",
          panel_type: this.defaultTypeForTab() || "clinic",
          password: "",
          password_confirmation: "",
        };
        await this.fetchIndividualDoctors();
      } catch (err) {
        const data = err?.response?.data;
        const firstError = data?.errors ? Object.values(data.errors)[0] : null;
        this.individualError =
          data?.message ||
          (Array.isArray(firstError) ? firstError[0] : firstError) ||
          "Ошибка при добавлении индивидуального врача";
        console.error(err);
      } finally {
        this.individualPending = false;
      }
    },
    async deleteIndividualDoctor(doctor) {
      if (!doctor?.id) return;

      const confirmed = confirm(`Удалить врача ${doctor.full_name}?`);
      if (!confirmed) return;

      this.$set
        ? this.$set(this.individualDeleting, doctor.id, true)
        : (this.individualDeleting[doctor.id] = true);
      this.individualError = "";

      try {
        await doctorService.deleteIndividualDoctor(doctor.id);
        await this.fetchIndividualDoctors();
      } catch (err) {
        const data = err?.response?.data;
        this.individualError =
          data?.message || "Ошибка при удалении индивидуального врача";
        console.error(err);
      } finally {
        this.$delete
          ? this.$delete(this.individualDeleting, doctor.id)
          : delete this.individualDeleting[doctor.id];
      }
    },
    async toggleActive(clinic) {
      const id = clinic.id;
      const next = !clinic.is_active;

      this.$set ? this.$set(this.pending, id, true) : (this.pending[id] = true);
      const prev = clinic.is_active;
      clinic.is_active = next;

      try {
        await clinicService.setActive(id, next);
      } catch (e) {
        clinic.is_active = prev;
        alert("Не удалось изменить статус клиники");
        console.error(e);
      } finally {
        this.$delete ? this.$delete(this.pending, id) : delete this.pending[id];
      }
    },
    toDateInput(value) {
      // "2026-08-17", "2026-08-17T00:00:00.000000Z" и "2026-08-17 00:00:00" → "2026-08-17"
      return value ? String(value).slice(0, 10) : "";
    },
    formatDate(value) {
      const [y, m, d] = this.toDateInput(value).split("-");
      return d && m && y ? `${d}.${m}.${y}` : value;
    },
    subscriptionDaysLeft(clinic) {
      const date = this.toDateInput(clinic.subscription_until);
      if (!date) return null;
      const end = new Date(`${date}T23:59:59`);
      return Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
    },
    getSubscriptionClass(clinic) {
      const days = this.subscriptionDaysLeft(clinic);
      if (days === null) return "bg-gray-100 text-gray-600";
      if (days <= 0) return "bg-amber-800 text-white";
      if (days <= 30) return "bg-yellow-400 text-white";
      return "bg-green-600 text-white";
    },
    setKey(obj, key, value) {
      this.$set ? this.$set(obj, key, value) : (obj[key] = value);
    },
    deleteKey(obj, key) {
      this.$delete ? this.$delete(obj, key) : delete obj[key];
    },
    startEditSubscription(clinic) {
      this.setKey(
        this.editingSubscription,
        clinic.id,
        this.toDateInput(clinic.subscription_until)
      );
    },
    cancelEditSubscription(clinic) {
      this.deleteKey(this.editingSubscription, clinic.id);
    },
    async saveSubscription(clinic) {
      const date = this.editingSubscription[clinic.id] || null;
      this.setKey(this.subscriptionSaving, clinic.id, true);
      try {
        await clinicService.setSubscriptionUntil(clinic.id, date);
        clinic.subscription_until = date;
        this.deleteKey(this.editingSubscription, clinic.id);
      } catch (e) {
        alert(e?.response?.data?.message || "Не удалось сохранить срок подписки");
        console.error(e);
      } finally {
        this.deleteKey(this.subscriptionSaving, clinic.id);
      }
    },
    async deleteClinic(id) {
      if (!confirm("Удалить эту клинику навсегда?")) return;
      try {
        await clinicService.remove(id);
        await this.fetchClinics();
      } catch (e) {
        alert("Ошибка при удалении клиники");
        console.error(e);
      }
    },
    async quickAddDoctor(clinic) {
      if (this.isBudgetFull(clinic)) {
        alert("В бюджетную клинику можно добавить максимум 5 врачей");
        return;
      }
      if (!this.selectedCategory) return;
      const fullName = prompt("Введите ФИО врача:");
      const experience = prompt("Введите опыт врача (в годах):");
      if (!fullName || !experience) return;

      try {
        await axios.post("/admin/clinics/doctors", {
          clinic_id: clinic.id,
          full_name: fullName,
          category_id: this.selectedCategory,
          experience: experience,
          is_active: true,
        });
        alert("Врач добавлен");
        await this.fetchClinics();
      } catch (error) {
        alert("Ошибка при добавлении врача");
        console.error(error);
      }
    },
  },
  mounted() {
    this.fetchClinics();
    this.fetchIndividualDoctors();
    this.fetchCategories();
  },
};
</script>
